import React, { useEffect, useMemo, useState } from 'react';
import { feedbackService, userService } from '../services/api';

const STATUS_OPTIONS = ['NEW', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'];
const TYPE_OPTIONS = ['BUG', 'IDEA', 'QUESTION', 'PRAISE'];
const TYPE_BADGE_CLASSES = {
  BUG: '',
  IDEA: '',
  QUESTION: '',
  PRAISE: ''
};
const TYPE_BADGE_STYLE = {
  BUG: {background:'rgba(230,100,97,0.15)',color:'rgba(230,100,97,0.9)',border:'1px solid rgba(230,100,97,0.3)'},
  IDEA: {background:'rgba(130,255,207,0.12)',color:'rgba(130,255,207,0.85)',border:'1px solid rgba(130,255,207,0.25)'},
  QUESTION: {background:'rgba(251,191,36,0.12)',color:'rgba(251,191,36,0.85)',border:'1px solid rgba(251,191,36,0.25)'},
  PRAISE: {background:'rgba(130,255,207,0.2)',color:'rgba(130,255,207,0.95)',border:'1px solid rgba(130,255,207,0.35)'}
};

const FeedbackAdminTab = () => {
  const [feedbackItems, setFeedbackItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    userId: '',
    startDate: '',
    endDate: ''
  });
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [copyStatus, setCopyStatus] = useState('');

  useEffect(() => {
    loadUsers();
    loadFeedback();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await userService.getAllUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadFeedback = async (customFilters = null) => {
    const activeFilters = customFilters || filters;
    try {
      setLoading(true);
      const response = await feedbackService.getFeedbackList({
        status: activeFilters.status || undefined,
        type: activeFilters.type || undefined,
        userId: activeFilters.userId || undefined,
        startDate: activeFilters.startDate || undefined,
        endDate: activeFilters.endDate || undefined,
        take: 200
      });
      setFeedbackItems(response.data || []);
    } catch (error) {
      console.error('Error loading feedback:', error);
      setMessage({ type: 'error', text: 'Failed to load feedback' });
    } finally {
      setLoading(false);
    }
  };

  const clearMessage = () => {
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  };

  const clearCopyStatus = () => {
    setTimeout(() => setCopyStatus(''), 2000);
  };

  useEffect(() => {
    if (message.text) clearMessage();
  }, [message]);

  useEffect(() => {
    if (copyStatus) clearCopyStatus();
  }, [copyStatus]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    loadFeedback();
  };

  const handleClearFilters = () => {
    const cleared = { status: '', type: '', userId: '', startDate: '', endDate: '' };
    setFilters(cleared);
    loadFeedback(cleared);
  };

  const handleStatusChange = async (feedbackId, newStatus) => {
    try {
      setUpdatingId(feedbackId);
      await feedbackService.updateFeedbackStatus(feedbackId, newStatus);
      setFeedbackItems(prev => prev.map(item =>
        item.id === feedbackId ? { ...item, status: newStatus } : item
      ));
      setMessage({ type: 'success', text: 'Feedback status updated' });
    } catch (error) {
      console.error('Error updating feedback status:', error);
      setMessage({ type: 'error', text: 'Failed to update feedback status' });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleOpenDetails = (item) => {
    setSelectedFeedback(item);
    setCopyStatus('');
  };

  const handleCloseDetails = () => {
    setSelectedFeedback(null);
    setCopyStatus('');
  };

  const handleCopyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyStatus('Copied');
    } catch (error) {
      console.error('Copy failed:', error);
      setCopyStatus('Copy failed');
    }
  };

  const formattedItems = useMemo(() => feedbackItems.map(item => ({
    ...item,
    createdAtDisplay: item.createdAt ? new Date(item.createdAt).toLocaleString() : ''
  })), [feedbackItems]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold" style={{color:'rgba(255,255,255,0.85)'}}>Feedback</h3>
          <p className="text-sm" style={{color:'rgba(255,255,255,0.6)'}}>
            Review user feedback, update status, and filter by key dimensions.
          </p>
        </div>
      </div>

      {message.text && (
        <div className="mb-4 p-3 rounded-lg" style={message.type==='success'?{background:'rgba(130,255,207,0.12)',border:'1px solid rgba(130,255,207,0.3)',color:'rgba(130,255,207,0.9)'}:{background:'rgba(230,100,97,0.12)',border:'1px solid rgba(230,100,97,0.3)',color:'rgba(230,100,97,0.9)'}}>
          {message.text}
        </div>
      )}

      <div className="rounded-lg p-4 mb-4" style={{background:'var(--tqa-surface)',border:'1px solid var(--tqa-border-dark)'}}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
            >
              <option value="">All</option>
              {STATUS_OPTIONS.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>Type</label>
            <select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
            >
              <option value="">All</option>
              {TYPE_OPTIONS.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>User</label>
            <select
              value={filters.userId}
              onChange={(e) => handleFilterChange('userId', e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
            >
              <option value="">All</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name || user.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
            />
          </div>
          <div>
            <label className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="mt-1 w-full rounded-lg px-3 py-2 text-sm" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <button
            onClick={handleClearFilters}
            className="btn-secondary px-3 py-2 text-sm"
          >
            Clear
          </button>
          <button
            onClick={handleApplyFilters}
            className="btn-primary px-4 py-2 text-sm"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor:'var(--tqa-green)'}}></div>
        </div>
      ) : (
        <div className="rounded-lg overflow-hidden" style={{background:'var(--tqa-surface)',border:'1px solid var(--tqa-border-dark)'}}>
          <div className="grid grid-cols-12 gap-4 px-4 py-3 text-xs font-semibold" style={{background:'var(--tqa-surface-2)',color:'rgba(255,255,255,0.5)'}}>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-3">Title</div>
            <div className="col-span-3">User</div>
            <div className="col-span-1">Info</div>
            <div className="col-span-1">Status</div>
          </div>
          {formattedItems.length > 0 ? (
            <div className="max-h-[calc(100vh-520px)] overflow-y-auto">
              {formattedItems.map(item => (
                <div key={item.id} className="grid grid-cols-12 gap-4 px-4 py-4 text-sm" style={{borderTop:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.7)'}}>
                  <div className="col-span-2" style={{color:'rgba(255,255,255,0.45)'}}>
                    {item.createdAtDisplay}
                  </div>
                  <div className="col-span-2">
                    <span className="px-2 py-1 rounded-full text-xs font-semibold" style={TYPE_BADGE_STYLE[item.type] || {background:'rgba(255,255,255,0.08)',color:'rgba(255,255,255,0.6)',border:'1px solid var(--tqa-border-dark)'}}>
                      {item.type}
                    </span>
                  </div>
                  <div className="col-span-3">
                    <div className="font-medium" style={{color:'rgba(255,255,255,0.85)'}}>{item.title}</div>
                    <div className="text-xs line-clamp-2" style={{color:'rgba(255,255,255,0.45)'}}>{item.description}</div>
                  </div>
                  <div className="col-span-3">
                    <div className="font-medium" style={{color:'rgba(255,255,255,0.85)'}}>{item.user?.name || 'Unknown'}</div>
                    <div className="text-xs" style={{color:'rgba(255,255,255,0.45)'}}>{item.user?.email || 'n/a'}</div>
                  </div>
                  <div className="col-span-1">
                    <button
                      type="button"
                      onClick={() => handleOpenDetails(item)}
                      style={{color:'rgba(255,255,255,0.45)',border:'1px solid var(--tqa-border-dark)',borderRadius:'50%'}}
                      onMouseEnter={e=>{e.currentTarget.style.color='var(--tqa-green)';e.currentTarget.style.borderColor='rgba(130,255,207,0.3)';e.currentTarget.style.background='rgba(130,255,207,0.08)'}}
                      onMouseLeave={e=>{e.currentTarget.style.color='rgba(255,255,255,0.45)';e.currentTarget.style.borderColor='var(--tqa-border-dark)';e.currentTarget.style.background='transparent'}}
                      className="inline-flex items-center justify-center w-8 h-8 transition-colors"
                      title="View details"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                  </div>
                  <div className="col-span-1">
                    <select
                      value={item.status}
                      onChange={(e) => handleStatusChange(item.id, e.target.value)}
                      disabled={updatingId === item.id}
                      className="w-full rounded-lg px-2 py-1 text-xs" style={{background:'var(--tqa-surface-2)',border:'1px solid var(--tqa-border-dark)',color:'rgba(255,255,255,0.85)'}}
                    >
                      {STATUS_OPTIONS.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" style={{color:'rgba(255,255,255,0.4)'}}>No feedback found</div>
          )}
        </div>
      )}

      {selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="rounded-lg shadow-xl p-6 w-[640px] max-w-[92vw] max-h-[90vh] overflow-y-auto" style={{background:'var(--tqa-surface)'}}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold" style={{color:'rgba(255,255,255,0.85)'}}>Feedback Details</h3>
                <p className="text-sm" style={{color:'rgba(255,255,255,0.55)'}}>Full context and metadata for this report.</p>
              </div>
              <button
                type="button"
                onClick={handleCloseDetails}
                style={{color:'rgba(255,255,255,0.4)'}}
                onMouseEnter={e=>e.currentTarget.style.color='rgba(255,255,255,0.75)'}
                onMouseLeave={e=>e.currentTarget.style.color='rgba(255,255,255,0.4)'}
                className="transition-colors"
                aria-label="Close feedback details"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-2">
                <span className="px-2 py-1 rounded-full text-xs font-semibold" style={TYPE_BADGE_STYLE[selectedFeedback.type] || {background:'rgba(255,255,255,0.08)',color:'rgba(255,255,255,0.6)',border:'1px solid var(--tqa-border-dark)'}}>
                  {selectedFeedback.type}
                </span>
                <span className="text-xs" style={{color:'rgba(255,255,255,0.4)'}}>{selectedFeedback.createdAtDisplay}</span>
              </div>

              <div>
                <h4 className="text-sm font-semibold" style={{color:'rgba(255,255,255,0.75)'}}>Title</h4>
                <p className="text-sm" style={{color:'rgba(255,255,255,0.7)'}}>{selectedFeedback.title}</p>
              </div>

              <div>
                <h4 className="text-sm font-semibold" style={{color:'rgba(255,255,255,0.75)'}}>Description</h4>
                <p className="text-sm whitespace-pre-wrap" style={{color:'rgba(255,255,255,0.7)'}}>{selectedFeedback.description}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <h4 className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>User</h4>
                  <p style={{color:'rgba(255,255,255,0.7)'}}>{selectedFeedback.user?.name || 'Unknown'}</p>
                  <p className="text-xs" style={{color:'rgba(255,255,255,0.4)'}}>{selectedFeedback.user?.email || 'n/a'}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>Status</h4>
                  <p style={{color:'rgba(255,255,255,0.7)'}}>{selectedFeedback.status}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>Conversation ID</h4>
                  <p className="break-words" style={{color:'rgba(255,255,255,0.7)'}}>{selectedFeedback.conversationId || 'n/a'}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>Message ID</h4>
                  <p className="break-words" style={{color:'rgba(255,255,255,0.7)'}}>{selectedFeedback.messageId || 'n/a'}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>Project</h4>
                  <p style={{color:'rgba(255,255,255,0.7)'}}>{selectedFeedback.project?.name || selectedFeedback.projectId || 'n/a'}</p>
                </div>
                <div>
                  <h4 className="text-xs font-semibold" style={{color:'rgba(255,255,255,0.55)'}}>Rating</h4>
                  <p style={{color:'rgba(255,255,255,0.7)'}}>{selectedFeedback.rating ?? 'n/a'}</p>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold" style={{color:'rgba(255,255,255,0.75)'}}>Context JSON</h4>
                  <button
                    type="button"
                    onClick={() => handleCopyText(JSON.stringify(selectedFeedback.contextData || {}, null, 2))}
                    style={{color:'var(--tqa-green)'}}
                    onMouseEnter={e=>e.currentTarget.style.opacity='0.75'}
                    onMouseLeave={e=>e.currentTarget.style.opacity='1'}
                    className="text-xs font-semibold"
                  >
                    Copy JSON
                  </button>
                </div>
                <pre className="mt-2 text-xs rounded-lg p-3 overflow-x-auto" style={{background: 'var(--tqa-bg)', color: 'rgba(255,255,255,0.85)', border: '1px solid var(--tqa-border-dark)'}}>
{JSON.stringify(selectedFeedback.contextData || {}, null, 2)}
                </pre>
                {copyStatus && (
                  <div className="text-xs mt-1" style={{color:'rgba(255,255,255,0.4)'}}>{copyStatus}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackAdminTab;
